import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';

import { encodeApiError } from '@sandbox-platform/shared-ui';

import { CommissionInvoiceApiService } from '../../../../services/commission-invoice-api.service';
import { ExpertApiService } from '../../../../services/expert-api.service';
import { OrganizationApiService } from '../../../../services/organization-api.service';
import { COMMISSION_INVOICE_PAYEE_TYPE, CommissionInvoicePayeeType } from '../../../../shared/definitons';
import {
  CommissionInvoiceDTO,
  CreateCommissionInvoiceDTO,
  PendingCommissionDTO,
} from '../../../../types/commission-invoice.types';
import { ExpertDTO } from '../../../../types/expert.types';
import { OrganizationDTO } from '../../../../types/organization.types';
import { InvoiceCreateParams, InvoiceStepsManager } from '../definitions';
import {
  commissionKey,
  initialState,
  PayeeInfo,
  sameInvoiceGroup,
  toISO,
} from './definitions';

export type { PayeeInfo };

export const CommissionInvoiceCreateStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (
      store,
      api = inject(CommissionInvoiceApiService),
      expertApi = inject(ExpertApiService),
      orgApi = inject(OrganizationApiService)
    ) => {
      const steps = new InvoiceStepsManager(
        () => store.step(),
        (step) => patchState(store, { step })
      );

      return {
        isPayeeStep(): boolean {
          return steps.isPayeeStep();
        },
        isCommissionsStep(): boolean {
          return steps.isCommissionsStep();
        },
        goToPayee(): void {
          steps.goToPayee();
        },
        goToCommissions(): void {
          steps.goToCommissions();
        },

        /**
         * Sets the payee type and clears any existing expert/org selection
         * @param type - CommissionInvoicePayeeType
         */
        setPayeeType(type: CommissionInvoicePayeeType): void {
          patchState(store, {
            payeeType: type,
            selectedExpert: null,
            selectedOrg: null,
          });
        },

        /**
         * Sets or clears the selected expert
         * @param expert - ExpertDTO or null
         */
        setSelectedExpert(expert: ExpertDTO | null): void {
          patchState(store, { selectedExpert: expert });
        },

        /**
         * Sets or clears the selected organization
         * @param org - OrganizationDTO or null
         */
        setSelectedOrg(org: OrganizationDTO | null): void {
          patchState(store, { selectedOrg: org });
        },

        /**
         * Resolves payee from query params; fetches expert or org by ID when needed,
         * pre-populates selection state, and loads pending commissions.
         * Returns early silently if params are insufficient.
         * @param params - InvoiceCreateParams
         * @returns Promise<void>
         */
        async prefillPayee(params: InvoiceCreateParams): Promise<void> {
          const { payeeType, payeeId, payeeName, commissionId } = params;

          if (!payeeType || (payeeType !== COMMISSION_INVOICE_PAYEE_TYPE.Company && !payeeId)) return;

          patchState(store, { payeeType });

          if (payeeType === COMMISSION_INVOICE_PAYEE_TYPE.Company) {
            const payeeInfo: PayeeInfo = {
              payeeType: COMMISSION_INVOICE_PAYEE_TYPE.Company,
              payeeId: payeeId ?? '',
              payeeName: payeeName ?? 'SNDBX International',
            };
            await this.loadPendingCommissions(
              payeeInfo,
              commissionId ?? undefined
            );
            return;
          }

          try {
            const resolvedPayeeId = payeeId ?? '';
            if (payeeType === COMMISSION_INVOICE_PAYEE_TYPE.ExpertIndividual) {
              const response = await firstValueFrom(
                expertApi.getById(resolvedPayeeId)
              );
              patchState(store, { selectedExpert: response.data });
            } else {
              const response = await firstValueFrom(
                orgApi.getById(resolvedPayeeId)
              );
              patchState(store, { selectedOrg: response.data });
            }

            const payeeInfo: PayeeInfo = {
              payeeType,
              payeeId: resolvedPayeeId,
              payeeName: payeeName ?? '',
            };
            await this.loadPendingCommissions(
              payeeInfo,
              commissionId ?? undefined
            );
          } catch {
            // Pre-fill failed — user stays on payee step to select manually
          }
        },

        /**
         * Fetches pending commissions for the given payee and advances to the commissions step.
         * Optionally pre-selects a specific commission by ID.
         * @param payeeInfo - PayeeInfo
         * @param preselectCommissionId - optional commission ID to pre-select
         * @returns Promise<void>
         */
        async loadPendingCommissions(
          payeeInfo: PayeeInfo,
          preselectCommissionId?: string
        ): Promise<void> {
          patchState(store, { loading: true, error: null, payeeInfo });

          try {
            const response = await firstValueFrom(
              api.pendingCommissions(payeeInfo.payeeType, payeeInfo.payeeId)
            );
            const items = response.data.items ?? [];
            const selectedKeys = preselectCommissionId
              ? new Set(
                  items
                    .filter((c) => c.commission_id === preselectCommissionId)
                    .map(commissionKey)
                )
              : new Set<string>();

            patchState(store, { pendingCommissions: items, selectedKeys });
            steps.goToCommissions();
          } catch (error) {
            patchState(store, {
              error: encodeApiError(
                error,
                'Failed to load pending commissions'
              ),
            });
          } finally {
            patchState(store, { loading: false });
          }
        },

        /**
         * Toggles selection of a pending commission; clears other selections if
         * the toggled commission belongs to a different invoice group
         * @param commission - PendingCommissionDTO
         */
        toggleSelection(commission: PendingCommissionDTO): void {
          const key = commissionKey(commission);
          const current = new Set(store.selectedKeys());

          if (current.has(key)) {
            current.delete(key);
          } else {
            const firstSelected = store
              .pendingCommissions()
              .find((c) => current.has(commissionKey(c)));
            if (firstSelected && !sameInvoiceGroup(firstSelected, commission)) {
              current.clear();
            }
            current.add(key);
          }

          patchState(store, { selectedKeys: current });
        },

        /**
         * Returns true if the given commission is currently selected
         * @param commission - PendingCommissionDTO
         * @returns boolean
         */
        isSelected(commission: PendingCommissionDTO): boolean {
          return store.selectedKeys().has(commissionKey(commission));
        },

        /**
         * Returns the sum of amount_minor for all selected commissions
         * @returns number
         */
        selectedTotal(): number {
          const keys = store.selectedKeys();
          return store
            .pendingCommissions()
            .filter((c) => keys.has(commissionKey(c)))
            .reduce((sum, c) => sum + c.amount_minor, 0);
        },

        /**
         * Returns the array of currently selected pending commissions
         * @returns PendingCommissionDTO[]
         */
        selectedCommissions(): PendingCommissionDTO[] {
          const keys = store.selectedKeys();
          return store
            .pendingCommissions()
            .filter((c) => keys.has(commissionKey(c)));
        },

        /**
         * Returns to the payee step
         */
        goBack(): void {
          steps.goToPayee();
        },

        /**
         * Creates and submits the invoice for the selected commissions
         * @param createKey - idempotency key for invoice creation
         * @param submitKey - idempotency key for invoice submission
         * @param currency - currency code
         * @param periodStart - optional period start date string
         * @param periodEnd - optional period end date string
         * @param dueDate - optional due date string
         * @param notes - optional notes
         * @returns Promise<CommissionInvoiceDTO | null>
         */
        async submit(
          createKey: string,
          submitKey: string,
          currency: string,
          periodStart?: string,
          periodEnd?: string,
          dueDate?: string,
          notes?: string
        ): Promise<CommissionInvoiceDTO | null> {
          const payeeInfo = store.payeeInfo();
          if (!payeeInfo) return null;

          const selected = store
            .pendingCommissions()
            .filter((c) => store.selectedKeys().has(commissionKey(c)));

          if (selected.length === 0) return null;

          patchState(store, { loading: true, error: null });

          try {
            const lineItems = selected.map((c) => ({
              commission_id: c.commission_id,
              description: `${c.referral_code} — ${c.commission_type}`,
              amount_minor: c.amount_minor,
              currency: c.currency,
            }));

            const createDto: CreateCommissionInvoiceDTO = {
              payee_type: payeeInfo.payeeType,
              ...(payeeInfo.payeeType === COMMISSION_INVOICE_PAYEE_TYPE.ExpertIndividual
                ? { payee_expert_id: payeeInfo.payeeId }
                : payeeInfo.payeeType === COMMISSION_INVOICE_PAYEE_TYPE.ExpertOrganization
                ? { payee_organization_id: payeeInfo.payeeId }
                : {}),
              payee_name: payeeInfo.payeeName,
              invoice_for: selected[0]?.invoice_for,
              line_items: lineItems,
              currency,
              ...(periodStart ? { period_start: toISO(periodStart) } : {}),
              ...(periodEnd ? { period_end: toISO(periodEnd) } : {}),
              ...(dueDate ? { due_date: toISO(dueDate) } : {}),
              ...(notes ? { notes } : {}),
            };

            const createResponse = await firstValueFrom(
              api.create(createDto, createKey)
            );
            const invoice = createResponse.data;

            const submitResponse = await firstValueFrom(
              api.submit(invoice.id, submitKey)
            );

            return submitResponse.data;
          } catch (error) {
            patchState(store, {
              error: encodeApiError(error, 'Failed to create invoice'),
            });
            return null;
          } finally {
            patchState(store, { loading: false });
          }
        },

        /**
         * Resets the store to its initial state
         */
        reset(): void {
          patchState(store, {
            payeeType: COMMISSION_INVOICE_PAYEE_TYPE.ExpertIndividual,
            selectedExpert: null,
            selectedOrg: null,
            payeeInfo: null,
            pendingCommissions: [],
            selectedKeys: new Set(),
            error: null,
          });
          steps.goToPayee();
        },

        /**
         * Clears the current error
         */
        clearError(): void {
          patchState(store, { error: null });
        },
      };
    }
  )
);
